/**
 * Every request the app makes, in one place.
 *
 * Three things the old code got wrong in five different ways, fixed once here:
 *
 * **Credentials.** The session is an HttpOnly cookie with no CSRF token and no
 * Authorization header, so every call has to send it. `fetch` does that by default for
 * same-origin requests, but the dev server runs on :5173 and proxies to :8770 — so it is
 * stated rather than assumed. Nothing here proves a POST is ours: the server refuses one
 * whose `Sec-Fetch-Site` or `Origin` — set by the browser, out of any script's reach — says
 * it came from another site, with `SameSite=Lax` on the cookie behind that. Through the Vite
 * proxy the page and the API are one origin, :5173, so the browser sends `same-origin` and
 * the proxy passes it on unchanged.
 *
 * **401.** A signed-out reader may read the calendar and may not write to it. The old page
 * grew a `res.status === 401` check at each call site, and the ones that were added later
 * did not have it: the briefing button and the heart both threw instead of offering to sign
 * in. It belongs here, once, and it carries `next` so signing in returns you to the thing
 * you were doing.
 *
 * **Shape.** The server sends JSON for its own routes and HTML for a login redirect. A
 * `.json()` on the latter throws a parse error naming a character position, which is how a
 * routing bug reads as malformed data.
 */

import type {
  FriendsResponse,
  NoticesResponse,
  WaitingResponse,
  Found,
  Profile,
  Viewer,
  ArtistPageResponse,
  CalendarBoot,
  HistoryClaimed,
  HistoryMonth,
  LatencyResponse,
  ArtistSearchResponse,
  EventHit,
  SearchResponse,
  VenuePayload,
  SuspectsResponse,
  GenrePayload,
  GenreIndex,
  EventDetailPublic,
  LandingPayload,
  ShareDraft,
  SharePayload,
  SharePool,
} from './types'
import { act, drew, type Action } from '~/beacon'

/** Thrown when the server wants a viewer and there is not one. Never a bug — a state. */
export class NeedsSignIn extends Error {
  readonly next: string

  constructor(next: string) {
    super('sign in')
    this.name = 'NeedsSignIn'
    this.next = next
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly path: string
  /**
   * What the server said, without the route and the code in front of it.
   *
   * `message` is for a log and says where and what; this is the half worth showing somebody.
   * A failed avatar printed `/me answered 503: ngrok gateway error … ERR_NGROK_3004` under
   * the button, which tells a reader nothing about their picture and everything about a
   * proxy they have never heard of.
   *
   * The server answers errors as `{"error": "..."}`, so that is unwrapped where it is there
   * and the raw text kept where it is not — a gateway failing in front of us does not
   * answer in our shape.
   */
  readonly detail: string

  constructor(status: number, path: string, detail: string) {
    super(`${path} answered ${status}: ${detail}`)
    this.name = 'ApiError'
    this.status = status
    this.path = path
    this.detail = said(detail)
  }
}

function said(detail: string): string {
  try {
    const held: unknown = JSON.parse(detail)
    if (held && typeof held === 'object' && typeof (held as { error?: unknown }).error === 'string') {
      return (held as { error: string }).error
    }
  } catch {
    // Not our JSON. A proxy's HTML page, or nothing at all.
  }
  return detail
}

function here(): string {
  return window.location.pathname + window.location.search
}

/** Requests through `call` still out. */
let out = 0

export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  out++
  try {
    const response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    })

    if (response.status === 401) throw new NeedsSignIn(here())

    const kind = response.headers.get('content-type') ?? ''
    if (!response.ok) {
      throw new ApiError(response.status, path, (await response.text()).slice(0, 200))
    }
    if (!kind.includes('json')) {
      // A page where a payload was expected means the route resolved somewhere else. Say so,
      // rather than letting JSON.parse report it as a character offset.
      throw new ApiError(response.status, path, `expected JSON, got ${kind || 'nothing'}`)
    }
    return (await response.json()) as T
  } finally {
    // The first time nothing is out, the page has drawn from what it asked for: here, before
    // the caller's own continuation, so the frame `drew` waits for has that answer in it.
    if (!--out) drew()
  }
}

/**
 * A write. Every write a reader makes comes through here, so this is where a gesture's
 * action starts, named by the address it posts to: a write added later is timed without an
 * edit, and an address outside the vocabulary is not timed at all. Started as the request
 * goes, after a caller that patches first has set its patch, so the next frame, which is
 * shown, is the one that draws it.
 */
function send<T>(path: string, body: unknown): Promise<T> {
  const settle = act((path === '/unfriend' ? 'friend' : path.slice(1)) as Action)
  const answer = call<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  answer.then(
    () => settle(true),
    () => settle(false),
  )
  return answer
}

/**
 * The `/landing` request in flight, or nothing.
 *
 * Two components ask for `/landing` when `/` opens — `ViewerProvider` to learn who is
 * asking, `Highlights` for the windows it draws — and each asks on mount, in the same tick.
 * While one request is pending a second caller is handed the same promise; the moment it
 * settles it is forgotten, so the next caller fetches again. A share, not a cache: nothing is
 * kept past the answer, so `Highlights` mounted later still asks fresh.
 *
 * Forgotten on rejection as much as on resolution. A layer that forgot only on success
 * would hold one bad answer for the rest of the page's life, and a front page that cannot
 * recover until reload.
 */
let landingInFlight: Promise<LandingPayload> | undefined

function landing(): Promise<LandingPayload> {
  if (landingInFlight) return landingInFlight
  const asked = call<LandingPayload>('/landing')
  landingInFlight = asked
  const forget = () => {
    // The slot is filled after `call()` returns and cleared on settlement, and nothing runs
    // between the two lines above, so by the time this runs the slot holds `asked` or
    // nothing: the guard is here for the shape (clear only your own request), not for a
    // case any caller can reach — only a `fetch` that re-entered `landing()` mid-dispatch
    // could put a later request in the slot first.
    if (landingInFlight === asked) landingInFlight = undefined
  }
  // `then(forget, forget)` rather than `finally(forget)`: the rejection handler here means
  // the derived promise settles clean, so a failed `/landing` is reported once, to the
  // callers holding `asked`, and never as an unhandled rejection from this bookkeeping.
  // `finally` would re-reject its own promise, which nobody holds.
  asked.then(forget, forget)
  return asked
}

export const api = {
  /** The front page's four windows. Public, like the page. One request however many ask
   *  while it is pending — see `landingInFlight`. */
  landing,

  /** One room. The page at `/v/<slug>` is what a reader has; this is what it asks for. */
  venue: (slug: string) => call<VenuePayload>(`/venuepage/${encodeURIComponent(slug)}`),

  /**
   * One share card's data. `rest` is the part of the page's address after `/share-page/`
   * and `search` its query, both handed on as they arrived: the server validates the key
   * and the `?on=` night, and answers 400 or 404 in its own words.
   */
  share: (rest: string, search: string) => call<SharePayload>(`/share-data/${rest}${search}`),

  /**
   * The candidates for one list card — the rule's own pool, more rows than the card draws.
   *
   * The editor's, and answered 403 for anybody else: it is the working-out behind a published
   * list rather than the list. `rest` is the card's own address, never a draft's — the pool is
   * what a card *could* draw and does not move when a selection does.
   */
  sharePool: (rest: string) => call<SharePool>(`/share-pool/${rest}`),

  /**
   * Choose the rows of one card, or change a choice already made.
   *
   * `card` and `query` are the card's own address split at the `?`, which is what the row
   * stores; `token` rewrites an existing draft and bumps its version, and its absence mints
   * one. The server refuses an id that is not one of that card's candidates, in its own words.
   */
  shareDraft: (body: { card: string; query: string; ids: string[]; token?: string }) =>
    send<ShareDraft>('/share-draft', body),

  /**
   * One card as the PNG the route drew, with the name the route gave it.
   *
   * Not `call`: this answer is an image, and every failure of it is `text/plain` — the render
   * route's own sentence, which is the only thing worth showing the person who pressed the
   * button. Wrapped in `ApiError` all the same, so `detail` is that sentence and nothing about
   * this function is in front of it.
   *
   * The name comes off `Content-Disposition` rather than being built here, because the route
   * builds it out of the card's identity: two variants of one card must not land on a phone
   * with one name, and the route is where that identity is known.
   */
  cardPng: async (rest: string): Promise<{ blob: Blob; filename: string }> => {
    const path = `/share/${rest}`
    const response = await fetch(path, { credentials: 'same-origin' })
    if (!response.ok) {
      throw new ApiError(response.status, path, (await response.text()).slice(0, 200))
    }
    const named = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')
    // The fallback is reached only if the route stops naming the file, which would be a
    // different bug; it is here so a missing header is a plainly-named download rather than
    // whatever the browser makes of a path with a query on it.
    return { blob: await response.blob(), filename: named?.[1] ?? 'london-gigs.png' }
  },

  /**
   * The whole ranked calendar. Redacted for everyone who is not the editor, which is why
   * the return type is the public row: `status`, `suggested` and `starred` are optional on
   * it, so reading one without checking will not typecheck.
   */
  /**
   * The calendar out to `to`, or all of it when nobody says; and, when `after` names the
   * night already held, only the rows past it. Everything else in the answer — `nights`,
   * `counts`, the span — is the whole window's either way, so a widening can take
   * those from the delta and append its rows to the ones it holds.
   *
   * Unbounded was the only mode once, and every row of the calendar was built, formatted and
   * gzipped so a month grid could draw a screen of chips; `to` is the calendar saying how
   * much of itself it is about to draw, and `after` is it saying how much it already has.
   */
  entries: (to?: string, after?: string) => {
    const query = new URLSearchParams()
    if (to) query.set('to', to)
    if (after) query.set('after', after)
    const asked = query.toString()
    return call<CalendarBoot>(asked ? `/entries?${asked}` : '/entries')
  },

  /**
   * One month of the Everything tab, whole, gone nights and nights ahead alike. Public, and
   * the same answer for everybody: the rows carry nobody's marks, so a signed-in reader's
   * own are laid over them from `/entries` in the browser rather than asked for here.
   */
  history: (month: string) => call<HistoryMonth>(`/history?month=${encodeURIComponent(month)}`),

  /**
   * This viewer's claimed rows on every night before `/entries`' `since`, in one body. Asked
   * for once, the first time a view draws a night that far back.
   */
  claimed: () => call<HistoryClaimed>('/history/claimed'),

  event: (id: string) => call<EventDetailPublic>(`/event/${encodeURIComponent(id)}`),

  artist: (id: number) => call<ArtistPageResponse>(`/artistpage/${id}`),

  /** One genre: who plays it and what they have coming up. The tag itself is the key —
   *  MusicBrainz's tags have spaces in them and slugging would need a reverse map. */
  genre: (name: string) => call<GenrePayload>(`/genrepage/${encodeURIComponent(name)}`),

  /** Every genre worth a page. Two acts minimum; see `calendar.genres`. */
  genres: () => call<GenreIndex>('/genres'),

  /** Everything the search knows: acts, rooms and festival brands. Public. */
  /** `limit` is how many of each kind: a few for the bar's dropdown, the lot for the
   *  results page. Same endpoint either way, so the two can never disagree about what
   *  matches — they differ only in how much they draw. */
  find: (query: string, limit?: number) =>
    call<SearchResponse>(
      `/find?q=${encodeURIComponent(query)}${limit ? `&limit=${limit}` : ''}`,
    ),

  /**
   * Acts only. What the bill editor asks on every keystroke — no reason to sweep the venues
   * and the festivals to answer it.
   */
  findActs: (query: string) =>
    call<ArtistSearchResponse>(`/find?kind=artists&q=${encodeURIComponent(query)}`),

  /**
   * The merge picker. Editor only, and the one search that reaches past the curated list —
   * a duplicate is very often a row nobody has decided about, which is the whole reason it
   * needs merging.
   */
  findEvents: (query: string) =>
    call<{ events: EventHit[] }>(`/find?kind=events&q=${encodeURIComponent(query)}`),

  save: (id: string, saved: boolean) => send<unknown>('/save', { id, saved }),

  going: (id: string, going: boolean) => send<unknown>('/going', { id, going }),

  /**
   * Follow an act, which adopts every gig they headline. The heart cannot take one of those
   * off the list — this is the control that can, which is why the mark on such a row is a
   * star rather than a heart.
   */
  /*
   * `{ artist, favourite }`, which is what the handler reads — `int(body["artist"])`,
   * `bool(body["favourite"])`. It sent `{ id, on }`, so every press was a KeyError and a 400
   * and nothing was ever written. `BillSection`'s own docstring has said the right shape all
   * along; the client never matched it.
   *
   * Reported three times as "can't star artists" and looked like a CSS bug each time,
   * because the artist page keeps optimistic local state — the star flipped there and stayed
   * flipped until a reload, which is exactly what a working control looks like.
   */
  star: (artistId: number, on: boolean) =>
    send<unknown>('/favourite', { artist: artistId, favourite: on }),

  /**
   * Follow a room: it joins your My venues tab and its nights rank up in your calendar. It
   * does not *add* them, which is the difference between this and starring an act.
   *
   * Anybody signed in, and it writes the caller's own row — moved out of the editor's block
   * below when a room became something a reader can follow. The editor's press also moves
   * the site-wide `starred` column, which the server decides; the body is the same either
   * way, and `starred` in it means "on", not that column.
   */
  trackVenue: (slug: string, on: boolean) => send<unknown>('/star', { slug, starred: on }),

  /* ---- the editor's writes. The server refuses them for anybody else; the components that
     offer them are not rendered for anybody else either, which is belt and braces on
     purpose: a control that 403s is a control that should not have been drawn. ---- */

  /** The acts that are not acts, busiest first. The editor's. */
  suspects: () => call<SuspectsResponse>('/suspects'),

  /**
   * Say what a row is. Four answers, because there are four things it can be.
   *
   * `artist` — real, the rule was wrong. `qualifier` — the bracket is a note about the set,
   * so the name is corrected and the note moves onto the booking. `describes` — the whole row
   * is a note that lost its name, like the `[LIVE]` in `Blawan + [LIVE] + Mala`, so it goes
   * onto the act before it. `placeholder` — not a named act and still part of the bill, like
   * a resident DJ. `neither` — not a person and not part of the bill, like `[A-Z]`. `split`
   * — several real acts in one row, so each goes onto the bills and the row comes off.
   *
   * `undo` takes one back. It answers `undone: 0` for a row that was folded away, because a
   * fold moves the dates onto another act and deletes this one — there is nothing left to
   * put back, and the page says so rather than pretending the press worked.
   *
   * The answer carries an artist id back because `qualifier` folds into the row that already
   * has the corrected name, which is usually a different one.
   */
  tidy: (
    artist: number,
    verdict:
      | 'artist'
      | 'qualifier'
      | 'describes'
      | 'split'
      // One act billed as its own halves. `name` carries the group to fold it into.
      | 'member'
      | 'placeholder'
      | 'neither'
      | 'undo',
    name?: string,
    qualifier?: string,
  ) =>
    send<{ ok: true; artist: number; undone: number; waiting: number }>('/tidy', {
      artist,
      verdict,
      name,
      qualifier,
    }),

  /**
   * Answer a row two spellings of one name were folded onto.
   *
   * `one` — a name spelled two ways by two promoters, `Emeli Sande` and `Emeli Sandé`. The row
   * is right; recorded so it is never offered again, and nothing about it changes. `two` — two
   * acts, and `spelling` names which of them to take off: every night billed that way moves
   * onto one new row under the next numbered slug, and the row left behind keeps its slug, its
   * name and its followers.
   *
   * Its own route rather than a `tidy` verdict: that one renames, folds and hides rows, and
   * neither answer here does any of the three.
   */
  spelling: (artist: number, verdict: 'one' | 'two', spelling?: string) =>
    send<{ ok: true; artist: number; waiting: number }>('/spelling', {
      artist,
      verdict,
      spelling,
    }),

  edit: (patch: { id: string } & Record<string, unknown>) => send<unknown>('/edit', patch),

  merge: (id: string, into: string | null) => send<unknown>('/merge', { id, into }),

  /**
   * Say that two artist rows are one act, and fold `fold` into `keep`.
   *
   * Editorial, like a merge: it moves every date the folded row held, teaches the keeper
   * its spelling so the next feed to use it lands in the right place, and deletes it.
   */
  absorb: (keep: number, fold: number) => send<{ ok: true }>('/absorb', { keep, fold }),
  /**
   * Move one night off an act because it is somebody else.
   *
   * The inverse of `absorb`. `slugify` folds accents, so `Adela` and `ADÉLA` are one slug
   * and the second listing to arrive lands on the first act's row — right far more often
   * than it is wrong, which is why the rule stays and this is how the exceptions get out.
   * The name is the editor's to give: nothing in the database knows who the other act was.
   */
  separate: (artist: number, event: string, name: string) =>
    send<{ ok: true; artist: number }>('/separate', { artist, event, name }),

  bill: (change: { id: string } & Record<string, unknown>) => send<unknown>('/bill', change),

  /**
   * Say a listing is not a real event, or put it back.
   *
   * `{ id, deleted, reason }`, which is what `_delete` reads: `body["id"]`,
   * `body.get("deleted", True)`, `body.get("reason")`. It sent `{ id, as }` and the handler
   * has never looked at `as`, so all three call sites were wrong in different ways —
   * refusing lost the reason, which is what `hide_known_plays` keys off, and restoring
   * *hid* the event, because `deleted` defaulted to true.
   *
   * `as` is the kind of not-real it is — a play, a duplicate, a mistake — except for
   * `restore`, which is the one value that means the opposite of hiding.
   */
  hide: (id: string, as: string | null) =>
    send<unknown>('/delete', { id, deleted: as !== 'restore', reason: as ?? 'deleted' }),

  /**
   * Everyone you are connected to, accepted or pending.
   *
   * There was no endpoint for this: the Python page baked the list into the document it
   * rendered, so when the app replaced that page the friends interface went with it —
   * nothing was deleted, there was simply no way left to ask.
   */
  friends: () => call<FriendsResponse>('/friends'),

  /** The two numbers the bar badges, in one request. Its own endpoint because the bar
   *  is on every page and `/entries` is the calendar's payload. */
  waiting: () => call<WaitingResponse>('/waiting'),

  /** How long the server took today, by what people do: the editor's, 403 for anybody else. */
  latency: () => call<LatencyResponse>('/latency.json'),

  /** Somebody's page. What comes back depends on who is asking; the server decides. */
  person: (username: string) => call<Profile>(`/person/${encodeURIComponent(username)}`),

  /** A name, a line, a face. Anything omitted is left alone rather than cleared. */
  me: (patch: { name?: string; bio?: string; avatar?: string }) =>
    send<{ ok: true; me: Viewer }>('/me', patch),

  /** Search for somebody to add. Empty query, empty list — the server does not guess. */
  people: (q: string) => call<Found[]>(`/people?q=${encodeURIComponent(q)}`),

  /**
   * Ask, or accept. One verb for both, because asking somebody who has already asked you
   * is an accept rather than a second request — otherwise two people who both reached out
   * would sit waiting for each other forever.
   *
   * Both of these answer with the whole list back rather than the row that changed:
   * accepting somebody moves them between sections and can settle a request in the other
   * direction at the same time.
   */
  befriend: (accountId: number) => send<FriendsResponse>('/friend', { account: accountId }),

  /**
   * Remove, decline, or cancel — one call, and the server decides which of the three.
   *
   * Deliberately not three verbs. Decline and Cancel send the same two account ids, and
   * only the direction of the stored row says which one it was; a client asked to label it
   * is a client that can label it wrongly, and the wrong label turns somebody turning a
   * request down into a refusal delivered to the person who sent it.
   */
  unfriend: (accountId: number) => send<FriendsResponse>('/unfriend', { account: accountId }),

  /** What has happened to you. Read-only — opening the panel is `noticed` below, so a
   *  prefetch cannot mark things read behind the reader. */
  notices: () => call<NoticesResponse>('/notices'),

  /** Say the panel has been looked at. Its own verb because looking changes something.
   *  `asOf` is the one `notices` answered with, so the mark moves to what was on screen and
   *  not to whatever has landed since; without one the server marks what it holds now. */
  noticed: (asOf?: string) =>
    send<{ ok: true }>('/noticed', asOf === undefined ? {} : { asOf }),
} as const
